import { escapeRegExp } from 'lodash'
import React, {
  FC,
  MouseEventHandler,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useReducer,
  useRef,
} from 'react'
import { Column, Renderer, CellProps, PluginHook } from 'react-table'
import { Signal } from '@bdswiss/mt4-connector/build/main/mt4-types'
import {MT4Connector, PricesExtended, TradeObject} from '@bdswiss/mt4-connector'
import { Translate, I18n } from 'react-redux-i18n'
import { VirtualizedTable } from './VirtualizedTable'
import { store } from '../index'
import { setTradingSignal } from '../actions/commonActions'
import { SignalPositionsTableContentContainer } from '../containers/SignalPositionsTable'
import { TableContext, expandedReducer } from './contexts/TableContext'
import { VariableSizeList } from 'react-window'
import { toast } from 'react-toastify'
import { humanReadableResolution } from '../lib/charting_library/utils'
import {RootState, SimpleSignal, TelegramSignal} from "../types";
import moment from "moment-timezone";
import {connect} from "react-redux";
import AssetsSelect from "./AssetsSelect";

const FavouriteCell: Renderer<CellProps<
  Signal,
  { favourite: boolean; confirmed: boolean; symbol: string }
>> = ({ value: { favourite, confirmed, symbol } }) => {
  const { onToggleFavourite } = useContext(TableContext)
  return (
    <i
      onClick={() => onToggleFavourite!(symbol)}
      className={`favourite ${favourite ? 'icon-star-full' : 'icon-star-empty'} ${
        !confirmed ? 'unconfirmed' : ''
      }`}
      title={I18n.t('favourite')}
      // data-cy={props.value}
    />
  )
}

const SymbolCell: Renderer<CellProps<Signal>> = ({ value, row }) => {
  const setSignal = useCallback(() => {
    store.dispatch(setTradingSignal(row.original))
  }, [row])
  if (!value) {
    return <div />
  }


  // @ts-ignore
  const txt = Object.values(MT4Connector.Instance.assets as any).find(x => x.symbol === value)?.label ?? value
  return (
    <div onClick={setSignal} className="bdswiss-table--bold" title={txt} data-cy={txt}>
      {txt}
    </div>
  )
}

const DirectionCell: Renderer<CellProps<TelegramSignal>> = ({ row }) => {
  const direction = row.original.type === 'LONG' ? 'up' : 'down'
  const setSignal = useCallback(() => {
    store.dispatch(setTradingSignal(row.original))
  }, [row])
  return (
    <div
      className={/up/i.test(direction) ? 'bdswiss-table--positive' : 'bdswiss-table--negative'}
      onClick={setSignal}
      title={row.original.type.toLowerCase()}
    >
      <i className={/down/i.test(direction) ? 'icon-buy-new' : 'icon-sell-new'} />
      <Translate value={row.original.type.toLowerCase()} />
    </div>
  )
}


const MessageCell: Renderer<CellProps<TelegramSignal>> = ({  row }) => {
  const setSignal = useCallback(() => {
    store.dispatch(setTradingSignal(row.original))
  }, [row])
  return <div onClick={setSignal}>{row.original.replies.map(x => <div>{x.reply}</div>)}</div>
}
const TextCell: Renderer<CellProps<TelegramSignal>> = ({value, row }) => {
  const setSignal = useCallback(() => {
    store.dispatch(setTradingSignal(row.original))
  }, [row])
  return <div onClick={setSignal}>{value}</div>
}
const DateCell: Renderer<CellProps<TelegramSignal>> = (cell) => {
  const setSignal = useCallback(() => {
    store.dispatch(setTradingSignal(cell.row.original))
  }, [cell.row])

  // const dates = [
  //     cell.row.original.timestamp,
  // ...cell.row.original.replies.map(x => x.timestamp)
  // ].map(x => moment(x))
  //     .sort((a,b) => a.date() < b.date() ? 1 : -1)

  const date = cell.row.original.timestamp
  const txt = moment(date).format('L')
  return <div onClick={setSignal}>{txt}</div>
}


const IdCell: Renderer<CellProps<any, string>> = ({ value }) => {
  const copyToClipboard = useCallback<MouseEventHandler>(
    e => {
      const textField = document.createElement('textarea')
      textField.innerText = value
      const parentElement = e.currentTarget
      parentElement!.appendChild(textField)
      textField.select()
      document.execCommand('copy')
      parentElement!.removeChild(textField)
      toast('Id copied to clipboard')
    },
    [value]
  )
  return <div onClick={copyToClipboard}>{value}</div>
}

const showMustGoOn = document.cookie
  .split(';')
  .map(c => c.trim())
  .filter(value => value.startsWith('showMustGoOn=true')).length

const initialState: { [signalId: string]: boolean } = {}
export interface TelegramSingnalTableProps {
  signals: TelegramSignal[]
  signal: TelegramSignal
  assets: PricesExtended
  favourites: Map<string, { confirmed: boolean; operation: 'on' | 'off' }>
  filter: string
  positions: TradeObject[],
  used: number,
  total: number
  // onToggleFavourite: (favourite: string) => void
}

const plugins: Array<PluginHook<{}>> = [
  hooks => {
    hooks.getCellProps.push((props, meta) => {
      if (meta.cell.column.id === 'favourite') {
        return { className: 'rt-td favourite-cell' }
      } else {
        return { className: 'rt-td' }
      }
    })
  },
]

export const TelegramSignalTableComponent: FC<TelegramSingnalTableProps> = ({
  signals,
  signal,
  favourites,
  filter,
  positions,
  used,
  total
  // onToggleFavourite,
}) => {
  const columns = useMemo<Array<Column<TelegramSignal>>>(() => {
    const newVar: Array<Column<TelegramSignal>> = [
      {
        Header: ' ',
        id: 'favourite',
        minWidth: 50,
        width: 50,
        accessor: ({ pair }) => {
          const favourite = favourites.get(pair)
          const confirmed = favourite ? favourite.confirmed : true
          return { favourite, confirmed, pair }
        },
        Cell: FavouriteCell,
      },
      {
        Header: 'asset',
        id: 'symbol',
        accessor: ({ pair }) => pair,
        minWidth: 30,
        width: 80,

        Cell: SymbolCell,
      },
      {
        Header: 'direction',
        id: 'direction',
        accessor: (row) => ({ id: row.messageId, direction: row.type }),
        minWidth: 30,
        width: 60,

        Cell: DirectionCell,
      },
      {
        Header: 'Leverage',
        minWidth: 40,
        accessor: (row) => row.leverage,
        Cell: TextCell,
      },
      {
        Header: 'Stop Loss',
        minWidth: 40,
        width:50,
        accessor: (row) => row.stopLoss,
        Cell: TextCell,
      },
      {
        Header: 'Place At',
        minWidth: 50,
        width:60,
        accessor: (row) => row.buyZone.map(x => <div>{x}</div>),
        Cell: TextCell,
      },
      {
        Header: 'Close At',
        minWidth: 40,
        width:60,
        accessor: (row) => row.sell.map(x => <div>{x}</div>),
        Cell: TextCell,
      },
      {
        Header: 'Updates',
        minWidth: 250,
        // accessor: (row) => (message),
        Cell: MessageCell,
      },
      {
        Header: 'Date',
        minWidth: 40,
        width: 55,
        // accessor: (row) => (date: new Date(row.timestamp)),
        Cell: DateCell,
      }
    ]
    return newVar
  }, [favourites])
  const data = useMemo(() => {
    const filtered =  filter
      ? Object.values(signals)
          .filter(({ pair, }) =>
            new RegExp(escapeRegExp(filter.replace('/', '')), 'i').test(
              [
                pair,
                // pattern ? 'Chart Pattern' : 'Key Level',
                // pattern ? pattern.name : keylevel!.type,
              ].join('|')
            )
          )
      : Object.values(signals)
    const sorted = filtered.sort((a, b) => (moment(a.timestamp).date() > moment(b.timestamp).date() ? -1 : 1))
    return sorted
  }, [signals, filter])

  const [expandedPositionsState, expandedPositionsDispatch] = useReducer(
    expandedReducer,
    initialState
  )
  const getItemSize = useCallback(
    (index: number) => {
      const signal = data[index]
      // const positionsSize = positions.filter(p => p.signalId === signal.id).length
      const maxData = Math.max(signal.replies.length,signal.buyZone.length,signal.sell.length)
      // if (positionsSize === 0) {
      //   return 100
      // }
      // if (positionsSize <= 3) {
      //   return 36 + 22 + positionsSize * 36
      // }
      return 36 + maxData* 23
    },
    [data, positions, expandedPositionsState]
  )
  const listRef = useRef<VariableSizeList>(null)
  useEffect(() => {
    if (listRef && listRef.current) {
      listRef.current.resetAfterIndex(0)
    }
  }, [listRef, positions.length, data.length])
  const signalId = signal && signal.messageId
  const isRowActive = useCallback(id => id === signalId, [signalId])

  return (
    <TableContext.Provider
      value={{
        keyPropertyName: 'id',
        // onToggleFavourite,
        expandedPositionsDispatch,
        expandedPositionsState,
        listRef,
        isRowActive,
      }}
    >
      <div style={{padding: '0px 20px'}}>
        <div>Signals: {used}/{total}</div>
      </div>
      <VirtualizedTable
        key={`autoSizer_${filter}`}
        columns={columns as Column<{}>[]}
        data={data}
        SubComponent={SignalPositionsTableContentContainer}
        getItemSize={getItemSize}
        plugins={plugins}
      />
    </TableContext.Provider>
  )
}

function mapStateToProps(rootState: RootState)  {

  return {
    signals: rootState.telegramSignals.signals,
    total: rootState.telegramSignals.clientStatistics.availableSignals,
    used: rootState.telegramSignals.clientStatistics.usedSignals
  }
}

export const TelegramSignalTable = connect(mapStateToProps, {})(TelegramSignalTableComponent)
